
import React from 'react';
import { Poll } from '@/types/poll';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Clock, Calendar, MapPin, Hash } from 'lucide-react';
import { useCountry } from '@/contexts/CountryContext';
import { cn } from '@/lib/utils';

interface PollPreviewProps {
  poll: Poll;
}

const PollPreview = ({ poll }: PollPreviewProps) => {
  const { availableCountries } = useCountry();
  
  const getCountryName = (code: string | null) => {
    if (!code) return 'Global';
    const country = availableCountries.find(c => c.code === code);
    return country ? country.name : code;
  };
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-400';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        {poll.poll_number && (
          <div className="flex items-center gap-1 mb-2">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 font-mono flex items-center gap-1">
              <Hash size={12} />
              {poll.poll_number}
            </Badge>
          </div>
        )}
        
        <h2 className="text-xl font-bold">{poll.title}</h2>
        <p className="text-gray-600 dark:text-gray-300">{poll.description}</p>
        
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge className={cn("font-normal", getStatusColor(poll.status))}>
            {poll.status === 'live' ? 'Active' : poll.status === 'draft' ? 'Draft' : 'Closed'}
          </Badge>
          
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <MapPin size={14} className="mr-1" />
            {getCountryName(poll.country_code)}
          </div>
          
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <Calendar size={14} className="mr-1" />
            Created: {formatDate(poll.created_at)}
          </div>
          
          {poll.closes_at && (
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock size={14} className="mr-1" />
              Closes: {formatDate(poll.closes_at)}
            </div>
          )}
        </div>
      </div>
      
      <div>
        <h3 className="font-medium mb-2">Poll Options</h3>
        <div className="space-y-2">
          {poll.options.map((option, index) => (
            <div key={option.id} className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
              <span className="h-5 w-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs mr-3">
                {index + 1}
              </span>
              <span>{option.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PollPreview;
